/*
Copyright 2023, 2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import {
  ConnectionError,
  ConnectionErrorReason,
  ConnectionState,
  type LocalTrack,
  type Room,
  RoomEvent,
  Track,
} from "livekit-client";
import { useCallback, useEffect, useRef, useState } from "react";
import { logger } from "matrix-js-sdk/lib/logger";
import { sleep } from "matrix-js-sdk/lib/utils";
import * as Sentry from "@sentry/react";

import { type SFUConfig, sfuConfigEquals } from "./openIDSFU";
import { PosthogAnalytics } from "../analytics/PosthogAnalytics";
import {
  ElementCallError,
  InsufficientCapacityError,
  SFURoomCreationRestrictedError,
  UnknownCallError,
} from "../utils/errors.ts";
import { AbortHandle } from "../utils/abortHandle.ts";
import { audioInput as audioInputSetting } from "../settings/settings";
import { callTraceJournal, describeTraceError, safeOrigin } from "../telemetry";
import {
  connectLiveKitOnce,
  publishPreparedLiveKitTracks,
} from "./LiveKitRecoveryAdapter";
import type { CallRecoveryPhase } from "../recovery/CallRecoveryTypes";

/*
 * Additional values for states that a call can be in, beyond what livekit
 * provides in ConnectionState. Also reconnects the call if the SFU Config
 * changes.
 */
export enum ECAddonConnectionState {
  // We are switching from one focus to another (or between livekit room aliases on the same focus)
  ECSwitchingFocus = "ec_switching_focus",
  // The call has just been initialised and is waiting for credentials to arrive before attempting
  // to connect. This distinguishes from the 'Disconnected' state which is now just for when livekit
  // gives up on connectivity and we consider the call to have failed.
  ECWaiting = "ec_waiting",
}

export type ECConnectionState = ConnectionState | ECAddonConnectionState;

export interface ECConnectionRecoveryDelegate {
  readonly isHardRecoveryActive: () => boolean;
  readonly getHardRecoveryPhase: () => CallRecoveryPhase;
  readonly subscribeHardRecoveryPhase: (
    listener: (phase: CallRecoveryPhase) => void,
  ) => () => void;
  readonly onInitialConnectionFailed: (error: unknown) => boolean;
  readonly onFocusSwitchFailed: (error: unknown) => void;
}

function isHardRecoveryPhase(phase: CallRecoveryPhase): boolean {
  return (
    phase === "recovering" ||
    phase === "waiting_for_network" ||
    phase === "verifying"
  );
}

// This is mostly necessary because an empty useRef is an empty object
// which is truthy, so we can't just use Boolean(currentSFUConfig.current)
function sfuConfigValid(sfuConfig?: SFUConfig): boolean {
  return Boolean(sfuConfig?.url) && Boolean(sfuConfig?.jwt);
}

const DEFAULT_AUDIO_INPUT_ID = "default";
const LIVEKIT_INITIAL_CONNECT_MAX_ATTEMPTS = 4;
const LIVEKIT_INITIAL_CONNECT_RETRY_DELAYS_MS = [100, 250, 500] as const;

interface AudioInputAttempt {
  deviceId: string | undefined;
  persistDeviceId?: string;
}

function buildAudioInputAttempts(
  initialDeviceId: string | undefined,
): AudioInputAttempt[] {
  const attempts: AudioInputAttempt[] = [{ deviceId: initialDeviceId }];

  if (
    initialDeviceId !== undefined &&
    initialDeviceId !== DEFAULT_AUDIO_INPUT_ID &&
    initialDeviceId !== ""
  ) {
    attempts.push({
      deviceId: DEFAULT_AUDIO_INPUT_ID,
      persistDeviceId: DEFAULT_AUDIO_INPUT_ID,
    });
  }

  return attempts;
}

function buildAudioConstraint(
  deviceId: string | undefined,
): true | MediaTrackConstraints {
  return deviceId === undefined ? true : { deviceId };
}

async function withTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number,
  timeoutMessage: string,
): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  try {
    return await Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        timeoutId = setTimeout(() => {
          reject(new Error(timeoutMessage));
        }, timeoutMs);
      }),
    ]);
  } finally {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
  }
}

function isRetryableLiveKitConnectError(
  error: unknown,
): error is ConnectionError {
  if (!(error instanceof ConnectionError)) return false;

  return (
    error.reason === ConnectionErrorReason.ServerUnreachable ||
    error.status === 200 ||
    error.status === 429 ||
    error.status === 503
  );
}

async function connectWithRetry(
  livekitRoom: Room,
  sfuConfig: SFUConfig,
): Promise<void> {
  let lastError: unknown;

  for (
    let attempt = 1;
    attempt <= LIVEKIT_INITIAL_CONNECT_MAX_ATTEMPTS;
    attempt += 1
  ) {
    const startedAt = performance.now();
    const relatedEventId = callTraceJournal.record({
      eventType: "livekit.connect_started",
      stage: "livekit",
      outcome: "started",
      payload: {
        attempt,
        maxAttempts: LIVEKIT_INITIAL_CONNECT_MAX_ATTEMPTS,
        livekitOrigin: safeOrigin(sfuConfig.url),
      },
    });
    try {
      logger.info(
        `[Lifecycle] Connecting to livekit room ${sfuConfig.url}, attempt ${attempt}/${LIVEKIT_INITIAL_CONNECT_MAX_ATTEMPTS} ...`,
      );
      await connectLiveKitOnce(livekitRoom, sfuConfig);
      logger.info("[Lifecycle] ... connected to livekit room");
      callTraceJournal.record({
        eventType: "livekit.connect_succeeded",
        stage: "livekit",
        outcome: "succeeded",
        relatedEventId,
        durationMs: Math.round(performance.now() - startedAt),
        payload: { attempt },
      });
      callTraceJournal.markConnectionConnected();
      return;
    } catch (error) {
      lastError = error;
      logger.error("[Lifecycle] Failed to connect", error);
      callTraceJournal.record({
        eventType: "livekit.connect_failed",
        stage: "livekit",
        severity: "error",
        outcome: "failed",
        relatedEventId,
        durationMs: Math.round(performance.now() - startedAt),
        payload: { attempt, ...describeTraceError(error) },
      });

      if (
        attempt === LIVEKIT_INITIAL_CONNECT_MAX_ATTEMPTS ||
        !isRetryableLiveKitConnectError(error)
      ) {
        callTraceJournal.finishConnection("failed", "initial_connect_failed");
        throw error;
      }

      const retryDelayMs = LIVEKIT_INITIAL_CONNECT_RETRY_DELAYS_MS[attempt - 1];
      callTraceJournal.record({
        eventType: "livekit.retry_scheduled",
        stage: "livekit",
        severity: "warn",
        outcome: "started",
        reasonCode: "retryable_connect_error",
        payload: { attempt, retryDelayMs },
      });
      logger.warn(
        `[Lifecycle] Повторяем подключение к LiveKit через ${retryDelayMs}мс`,
        {
          attempt,
          maxAttempts: LIVEKIT_INITIAL_CONNECT_MAX_ATTEMPTS,
          status: error.status,
          reason: error.reasonName,
        },
      );
      await sleep(retryDelayMs);
    }
  }

  throw lastError;
}

async function doConnect(
  livekitRoom: Room,
  sfuConfig: SFUConfig,
  audioEnabled: boolean,
  initialDeviceId: string | undefined,
  abortHandle: AbortHandle,
): Promise<void> {
  // Always create an audio track manually.
  // livekit (by default) keeps the mic track open when you mute, but if you start muted,
  // doesn't publish it until you unmute. We want to publish it from the start so we're
  // always capturing audio: it helps keep bluetooth headsets in the right mode and
  // mobile browsers to know we're doing a call.
  if (
    livekitRoom!.localParticipant.getTrackPublication(Track.Source.Microphone)
  ) {
    logger.warn(
      "Pre-creating audio track but participant already appears to have an microphone track: this shouldn't happen!",
    );
    Sentry.captureMessage(
      "Pre-creating audio track but participant already appears to have an microphone track!",
    );
    return;
  }

  logger.info("Pre-creating microphone track");
  const precreateStartedAt = performance.now();
  const precreateSelection =
    initialDeviceId === undefined
      ? "browser"
      : initialDeviceId === "default" || initialDeviceId === ""
        ? "default"
        : "specific";
  const precreateEventId = callTraceJournal.record({
    eventType: "media.audio_precreate_started",
    stage: "media",
    outcome: "started",
    payload: { selection: precreateSelection },
  });
  let preCreatedAudioTrack: LocalTrack | undefined;
  let lastPreCreateError: unknown;
  try {
    for (const attempt of buildAudioInputAttempts(initialDeviceId)) {
      try {
        const audioTracks = await withTimeout(
          livekitRoom!.localParticipant.createTracks({
            audio: buildAudioConstraint(attempt.deviceId),
          }),
          8000,
          `localParticipant.createTracks(audio) timed out after 8000ms for device ${
            attempt.deviceId ?? "browser-default"
          }`,
        );

        if (audioTracks.length < 1) {
          throw new Error("createTracks(audio) returned no tracks");
        }

        preCreatedAudioTrack = audioTracks[0];

        if (attempt.persistDeviceId !== undefined) {
          audioInputSetting.setValue(attempt.persistDeviceId);
          livekitRoom.options.audioCaptureDefaults = {
            ...livekitRoom.options.audioCaptureDefaults,
            deviceId: attempt.persistDeviceId,
          };
          logger.warn(
            "[Lifecycle] Аудиовход автоматически переключен на browser default после сбоя конкретного устройства",
            {
              failedDeviceId: initialDeviceId,
              fallbackDeviceId: attempt.persistDeviceId,
            },
          );
          callTraceJournal.record({
            eventType: "media.audio_device_fallback",
            stage: "media",
            severity: "warn",
            outcome: "recovered",
            reasonCode: "selected_device_failed",
            payload: { fallback: "default" },
          });
        }
        break;
      } catch (error) {
        lastPreCreateError = error;
        if (attempt.persistDeviceId === undefined) {
          logger.warn(
            "[Lifecycle] Не удалось подготовить микрофон для выбранного устройства, пробуем browser default",
            {
              failedDeviceId: initialDeviceId,
            },
          );
        }
      }
    }

    if (
      preCreatedAudioTrack === undefined &&
      lastPreCreateError !== undefined
    ) {
      throw lastPreCreateError;
    }

    // There was a yield point previously (awaiting for the track to be created) so we need to check
    // if the operation was cancelled and stop connecting if needed.
    if (abortHandle.isAborted()) {
      logger.info(
        "[Lifecycle] Signal Aborted: Pre-created audio track but connection aborted",
      );
      preCreatedAudioTrack?.stop();
      return;
    }

    logger.info("Pre-created microphone track");
    callTraceJournal.record({
      eventType: "media.audio_precreate_succeeded",
      stage: "media",
      outcome: "succeeded",
      relatedEventId: precreateEventId,
      durationMs: Math.round(performance.now() - precreateStartedAt),
      payload: { trackCreated: preCreatedAudioTrack !== undefined },
    });
  } catch (e) {
    callTraceJournal.record({
      eventType: "media.audio_precreate_failed",
      stage: "media",
      severity: "warn",
      outcome: "failed",
      relatedEventId: precreateEventId,
      durationMs: Math.round(performance.now() - precreateStartedAt),
      payload: {
        selection: precreateSelection,
        ...describeTraceError(e),
      },
    });
    if (e instanceof DOMException) {
      callTraceJournal.record({
        eventType: "media.permission_result",
        stage: "media",
        severity: e.name === "NotAllowedError" ? "error" : "warn",
        outcome: "failed",
        reasonCode: e.name,
        payload: { kind: "microphone" },
      });
    }
    logger.error("Failed to pre-create microphone track", e);
  }

  if (!audioEnabled) {
    await preCreatedAudioTrack?.mute();
    // There was a yield point. Check if the operation was cancelled and stop connecting.
    if (abortHandle.isAborted()) {
      logger.info(
        "[Lifecycle] Signal Aborted: Pre-created audio track but connection aborted",
      );
      preCreatedAudioTrack?.stop();
      return;
    }
  }

  // check again having awaited for the track to create
  if (
    livekitRoom!.localParticipant.getTrackPublication(Track.Source.Microphone)
  ) {
    logger.warn(
      "Pre-created audio track but participant already appears to have an microphone track: this shouldn't happen!",
    );
    preCreatedAudioTrack?.stop();
    return;
  }

  logger.info("[Lifecycle] Connecting & publishing");
  try {
    await connectAndPublish(livekitRoom, sfuConfig, preCreatedAudioTrack, []);
    if (abortHandle.isAborted()) {
      logger.info(
        "[Lifecycle] Signal Aborted: Connected but operation was cancelled. Force disconnect",
      );
      livekitRoom?.disconnect().catch((err) => {
        logger.error("Failed to disconnect from SFU", err);
      });
      return;
    }
  } catch (e) {
    preCreatedAudioTrack?.stop();
    logger.debug("Stopped precreated audio tracks.");
    throw e;
  }
}

/**
 * Connect to the SFU and publish specific tracks, if provided.
 * This is very specific to what we need to do: for instance, we don't
 * currently have a need to prepublish video tracks. We just prepublish
 * a mic track at the start of a call and copy any srceenshare tracks over
 * when switching focus (because we can't re-acquire them without the user
 * going through the dialog to choose them again).
 */
async function connectAndPublish(
  livekitRoom: Room,
  sfuConfig: SFUConfig,
  micTrack: LocalTrack | undefined,
  screenshareTracks: MediaStreamTrack[],
): Promise<void> {
  const tracker = PosthogAnalytics.instance.eventCallConnectDuration;
  // Track call connect duration
  tracker.cacheConnectStart();
  livekitRoom.once(RoomEvent.SignalConnected, tracker.cacheWsConnect);

  try {
    await connectWithRetry(livekitRoom, sfuConfig);
  } catch (e) {
    // LiveKit uses 503 to indicate that the server has hit its track limits.
    // https://github.com/livekit/livekit/blob/fcb05e97c5a31812ecf0ca6f7efa57c485cea9fb/pkg/service/rtcservice.go#L171
    // It also errors with a status code of 200 (yes, really) for room
    // participant limits.
    // LiveKit Cloud uses 429 for connection limits.
    // Either way, all these errors can be explained as "insufficient capacity".
    if (e instanceof ConnectionError) {
      if (e.status === 503 || e.status === 200 || e.status === 429) {
        throw new InsufficientCapacityError();
      }
      if (e.status === 404) {
        // error msg is "Could not establish signal connection: requested room does not exist"
        // The room does not exist. There are two different modes of operation for the SFU:
        // - the room is created on the fly when connecting (livekit `auto_create` option)
        // - Only authorized users can create rooms, so the room must exist before connecting (done by the auth jwt service)
        // In the first case there will not be a 404, so we are in the second case.
        throw new SFURoomCreationRestrictedError();
      }
    }
    throw e;
  }

  // remove listener in case the connect promise rejects before `SignalConnected` is emitted.
  livekitRoom.off(RoomEvent.SignalConnected, tracker.cacheWsConnect);
  tracker.track({ log: true });

  logger.info(
    `Publishing ${screenshareTracks.length} precreated screenshare tracks`,
  );
  await publishPreparedLiveKitTracks(livekitRoom, micTrack, screenshareTracks);
}

export function useECConnectionState(
  initialDeviceId: string | undefined,
  initialAudioEnabled: boolean,
  livekitRoom?: Room,
  sfuConfig?: SFUConfig,
  recovery?: ECConnectionRecoveryDelegate,
): ECConnectionState {
  const [connState, setConnState] = useState(
    sfuConfig && livekitRoom
      ? livekitRoom.state
      : ECAddonConnectionState.ECWaiting,
  );

  const [isSwitchingFocus, setSwitchingFocus] = useState(false);
  const [isInDoConnect, setIsInDoConnect] = useState(false);
  const [hardRecoveryPhase, setHardRecoveryPhase] = useState<CallRecoveryPhase>(
    () => recovery?.getHardRecoveryPhase() ?? "idle",
  );
  const [error, setError] = useState<ElementCallError | null>(null);
  const previousConnectionState = useRef<ConnectionState | null>(
    livekitRoom?.state ?? null,
  );
  const connectionStateChangedAt = useRef(performance.now());
  if (error !== null) throw error;

  useEffect(() => {
    if (!recovery) {
      setHardRecoveryPhase("idle");
      return;
    }
    return recovery.subscribeHardRecoveryPhase(setHardRecoveryPhase);
  }, [recovery]);

  const onConnStateChanged = useCallback((state: ConnectionState) => {
    const now = performance.now();
    callTraceJournal.record({
      eventType: "livekit.connection_state_changed",
      stage: "livekit",
      severity: state === ConnectionState.Disconnected ? "warn" : "info",
      outcome: "state_changed",
      durationMs: Math.round(now - connectionStateChangedAt.current),
      payload: { previous: previousConnectionState.current, state },
    });
    previousConnectionState.current = state;
    connectionStateChangedAt.current = now;
    if (state == ConnectionState.Connected) setSwitchingFocus(false);
    setConnState(state);
  }, []);

  useEffect(() => {
    const oldRoom = livekitRoom;

    if (livekitRoom) {
      previousConnectionState.current = livekitRoom.state;
      connectionStateChangedAt.current = performance.now();
      livekitRoom.on(RoomEvent.ConnectionStateChanged, onConnStateChanged);
    }

    return (): void => {
      if (oldRoom)
        oldRoom.off(RoomEvent.ConnectionStateChanged, onConnStateChanged);
    };
  }, [livekitRoom, onConnStateChanged]);

  const doFocusSwitch = useCallback(async (): Promise<void> => {
    const screenshareTracks: MediaStreamTrack[] = [];
    for (const t of livekitRoom!.localParticipant.videoTrackPublications.values()) {
      if (t.track && t.source == Track.Source.ScreenShare) {
        const newTrack = t.track.mediaStreamTrack.clone();
        newTrack.enabled = true;
        screenshareTracks.push(newTrack);
      }
    }

    // Flag that we're currently switching focus. This will get reset when the
    // connection state changes back to connected in onConnStateChanged above.
    setSwitchingFocus(true);
    callTraceJournal.finishConnection("superseded", "focus_switch");
    await livekitRoom?.disconnect();
    callTraceJournal.startConnection("focus_switch");
    setIsInDoConnect(true);
    try {
      await connectAndPublish(
        livekitRoom!,
        sfuConfig!,
        undefined,
        screenshareTracks,
      );
    } finally {
      setIsInDoConnect(false);
    }
  }, [livekitRoom, sfuConfig]);

  const currentSFUConfig = useRef(Object.assign({}, sfuConfig));
  const previousHardRecoveryPhase = useRef<CallRecoveryPhase>("idle");
  const adoptNextValidConfig = useRef(false);

  // Protection against potential leaks, where the component to be unmounted and there is
  // still a pending doConnect promise. This would lead the user to still be in the call even
  // if the component is unmounted.
  const abortHandlesBag = useRef(new Set<AbortHandle>());

  // This is a cleanup function that will be called when the component is about to be unmounted.
  // It will cancel all abortHandles in the bag
  useEffect(() => {
    const bag = abortHandlesBag.current;
    return (): void => {
      bag.forEach((handle) => {
        handle.abort();
      });
    };
  }, []);

  // Id we are transitioning from a valid config to another valid one, we need
  // to explicitly switch focus
  useEffect(() => {
    const wasHardRecoveryActive = isHardRecoveryPhase(
      previousHardRecoveryPhase.current,
    );
    previousHardRecoveryPhase.current = hardRecoveryPhase;
    if (isHardRecoveryPhase(hardRecoveryPhase)) {
      // Не запоминаем suppressed config: после success он принимается отдельно,
      // а terminal/cancelled ниже поглощают lifecycle до нового mount.
      return;
    }
    if (hardRecoveryPhase === "cancelled" || hardRecoveryPhase === "terminal") {
      // Эти состояния завершают текущую call session. До нового mount legacy
      // lifecycle не должен ни подключаться, ни переключать focus.
      return;
    }
    if (wasHardRecoveryActive && hardRecoveryPhase === "connected") {
      if (sfuConfigValid(sfuConfig)) {
        currentSFUConfig.current = Object.assign({}, sfuConfig);
        adoptNextValidConfig.current = false;
      } else {
        adoptNextValidConfig.current = true;
      }
      return;
    }
    if (adoptNextValidConfig.current && sfuConfigValid(sfuConfig)) {
      currentSFUConfig.current = Object.assign({}, sfuConfig);
      adoptNextValidConfig.current = false;
      return;
    }
    if (
      sfuConfigValid(sfuConfig) &&
      sfuConfigValid(currentSFUConfig.current) &&
      !sfuConfigEquals(currentSFUConfig.current, sfuConfig)
    ) {
      logger.info(
        `SFU config changed! URL was ${currentSFUConfig.current?.url} now ${sfuConfig?.url}`,
      );
      callTraceJournal.record({
        eventType: "sfu.focus_changed",
        stage: "sfu",
        outcome: "state_changed",
        payload: {
          previousOrigin: safeOrigin(currentSFUConfig.current?.url),
          currentOrigin: safeOrigin(sfuConfig?.url),
        },
      });

      doFocusSwitch().catch((e) => {
        logger.error("Failed to switch focus", e);
        if (recovery) recovery.onFocusSwitchFailed(e);
      });
    } else if (
      !sfuConfigValid(currentSFUConfig.current) &&
      sfuConfigValid(sfuConfig)
    ) {
      // if we're transitioning from an invalid config to a valid one (ie. connecting)
      // then do an initial connection, including publishing the microphone track:
      // livekit (by default) keeps the mic track open when you mute, but if you start muted,
      // doesn't publish it until you unmute. We want to publish it from the start so we're
      // always capturing audio: it helps keep bluetooth headsets in the right mode and
      // mobile browsers to know we're doing a call.
      callTraceJournal.startConnection("initial_connect");
      setIsInDoConnect(true);
      const abortHandle = new AbortHandle();
      abortHandlesBag.current.add(abortHandle);
      doConnect(
        livekitRoom!,
        sfuConfig!,
        initialAudioEnabled,
        initialDeviceId,
        abortHandle,
      )
        .catch((e) => {
          if (recovery?.onInitialConnectionFailed(e)) {
            logger.error(
              "Стандартное подключение к SFU не удалось, запускаем hard recovery",
              e,
            );
            return;
          }
          if (e instanceof ElementCallError) {
            setError(e); // Bubble up any error screens to React
          } else if (e instanceof Error) {
            setError(new UnknownCallError(e));
          } else logger.error("Failed to connect to SFU", e);
        })
        .finally(() => {
          abortHandlesBag.current.delete(abortHandle);
          setIsInDoConnect(false);
        });
    }

    currentSFUConfig.current = Object.assign({}, sfuConfig);
  }, [
    sfuConfig,
    livekitRoom,
    initialDeviceId,
    initialAudioEnabled,
    doFocusSwitch,
    hardRecoveryPhase,
    recovery,
  ]);

  // Because we create audio tracks by hand, there's more to connecting than
  // just what LiveKit does in room.connect, and we should continue to return
  // ConnectionState.Connecting for the entire duration of the doConnect promise
  return isSwitchingFocus
    ? ECAddonConnectionState.ECSwitchingFocus
    : isInDoConnect
      ? ConnectionState.Connecting
      : connState;
}
